import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {from, Observable, of, Subject} from "rxjs";
import {catchError, map, switchMap} from "rxjs/operators";
import {ToastService} from "../core/toast.service";
import {AdminDeliverySimulatorService, DeliverySimulatorResponse} from "../admin/admin-delivery-simulator.service";

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private subject: Subject<void> = new Subject<void>();
  private api: string;
  private cachedError = {
    POST: {},
    GET: {},
    DELETE: {}
  };
  private localPort = ':420';
  private devPort = ':421';
  private masterPort = ':422';
  private devTerminalKey = '371c2aea-5ad4-498f-a79c-e211485a5107';
  private masterTerminalKey = '17d893a1-90c2-470d-ae35-a1e3dd9c8bee';
  private devApi = 'https://dev.api.kiwi-germany.com/';
  private masterApi = 'https://api.kiwi-delivery.net/';

  private ready: Promise<any>;

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private simulator: AdminDeliverySimulatorService
  ) {
    this.ready = this.setUrl();
  }

  async setUrl() {
    try {
      if (location.href.indexOf(this.localPort) !== -1) {
        // a locally started app uses the local api (same host, so it works from other devices as well), another api on
        // purpose with localStorage 'kiwi-api' = 'dev' or 'master'. Without a running local api the production api is
        // used, with a warning (it has the real data)
        const localApi = `http://${location.hostname}:3000/`;
        this.api = this.getLocalOverride() || (await this.isReachable(localApi) ? localApi : this.masterApi);
        if (this.api === this.masterApi && !this.getLocalOverride()) {
          console.warn('[Kiwi] Lokale API nicht erreichbar, es wird die Production-API genutzt:', this.api);
          this.toastService.presentWarning('Lokale API nicht erreichbar – die App nutzt die Production-API (echte Daten).');
        }
        console.info('[Kiwi] API:', this.api);
      } else if (location.href.indexOf('dev.') !== -1 || location.href.indexOf(this.devPort) !== -1) {
        this.api = this.devApi;
      } else {
        this.api = this.masterApi;
      }
      return 'done';
    } catch (error) {
      console.log(error);
    }
  }

  // the api answers its health check within 1.5 seconds
  private async isReachable(api: string): Promise<boolean> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 1500);
    try {
      // no-cors: only reachability counts, a missing cors header of the local api must not count as down
      await fetch(api + 'health', {signal: controller.signal, mode: 'no-cors'});
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  private getLocalOverride(): string {
    try {
      const value = localStorage.getItem('kiwi-api');
      return value === 'master' ? this.masterApi : value === 'dev' ? this.devApi : null;
    } catch {
      return null;
    }
  }


  isLocalhost(): boolean {
    return location.href.indexOf('localhost:') !== -1;
  }

  getTerminalKey()  {
    return this.api === this.masterApi ? this.masterTerminalKey : this.devTerminalKey;
  }

  isTerminal(): boolean {
    return location.href.indexOf('terminal.') !== -1
      || location.href.indexOf(this.localPort + '1') !== -1
      || location.href.indexOf(this.devPort + '1') !== -1
      || location.href.indexOf(this.masterPort + '1') !== -1
  }

  getUrl() {
    return this.api;
  }

  public get Unauthorized(): Observable<void>  {
    return this.subject.asObservable();
  }

  public getErrorMessage(type: string, path: string): any {
    return this.cachedError[type]?.[path];
  }

  public async get(path: string, queryParams: {} = {}, disableWarning = true, successMessage: string = '', logout: boolean = true): Promise<any> {
    const simulated = this.simulator.handle('GET', path);
    if (simulated.Handled) {
      return this.handleSimulated(simulated, 'GET', path, successMessage, !disableWarning);
    }
    try {
      await this.ready;
      const response = await this.http.get(this.api + path, { params: queryParams, observe: 'response' } ).toPromise();
      await this.toastService.presentSuccess(successMessage);
      return response.body;
    } catch (e) {
      if (logout && e.status === 401) {
        this.subject.next();
      } else if (!disableWarning) {
        this.toastService.presentWarning(e);
      }
      this.cachedError.GET[path] = e.error;
      return null;
    }
  }

  // wie get(), aber als Observable statt Promise: ein subscribe() liefert kein toPromise-Deadlock,
  // ein unsubscribe() bricht die noch laufende HTTP-Anfrage tatsächlich ab (statt sie nur zu ignorieren)
  public getCancelable(path: string, queryParams: {} = {}, logout: boolean = true): Observable<any> {
    const simulated = this.simulator.handle('GET', path);
    if (simulated.Handled) {
      return from(this.handleSimulated(simulated, 'GET', path, '', false));
    }
    return from(this.ready).pipe(
      switchMap(() => this.http.get(this.api + path, { params: queryParams, observe: 'response' })),
      map((response: any) => response.body),
      catchError((e) => {
        if (logout && e.status === 401) {
          this.subject.next();
        }
        this.cachedError.GET[path] = e.error;
        return of(null);
      })
    );
  }

  public async postParams(path: string, object: any, params: any = null, successMessage: string = '', showErrorMessage: boolean = true, logout: boolean = true): Promise<any> {
    const simulated = this.simulator.handle('POST', path, object);
    if (simulated.Handled) {
      return this.handleSimulated(simulated, 'POST', path, successMessage, showErrorMessage);
    }
    try {
      await this.ready;
      const responseObject = await this.http.post(this.api + path, object, { params: params }).toPromise();
      await this.toastService.presentSuccess(successMessage);
      return responseObject;
    } catch (e) {
      if (logout && e.status === 401) {
        this.subject.next();
      } else if (e.status === 423 && showErrorMessage)  {
        await this.toastService.presentError(e.error || 'Die Funktion ist für dich deaktiviert.');
      } else if (showErrorMessage) {
        await this.toastService.presentError(e.error || 'Es wurden nicht genügend Angaben gemacht');
      }
      this.cachedError.POST[path] = e.error;
      return null;
    }
  }

  public async post(path: string, object: any, successMessage: string = '', showErrorMessage: boolean = true, logout: boolean = true): Promise<any> {
    return this.postParams(path, object, null, successMessage, showErrorMessage, logout);
  }

  public async delete(path: string, successMessage: string = '', showErrorMessage: boolean = true, logout: boolean = true): Promise<any>  {
    return this.deleteParams(path, null, successMessage, showErrorMessage, logout);
  }

  public async deleteParams(path: string, params: any = null, successMessage: string = '', showErrorMessage: boolean = true, logout: boolean = true): Promise<any>  {
    const simulated = this.simulator.handle('DELETE', path);
    if (simulated.Handled) {
      return this.handleSimulated(simulated, 'DELETE', path, successMessage, showErrorMessage);
    }
    try {
      await this.ready;
      const responseObject = await this.http.delete(this.api + path, { params: params }).toPromise();
      await this.toastService.presentSuccess(successMessage);
      return responseObject;
    } catch (e) {
      if (logout && e.status === 401) {
        this.subject.next();
      } else if (e.status !== 423 && showErrorMessage) {
        await this.toastService.presentError(e.error);
      }
      this.cachedError.DELETE[path] = e.error;
      return null;
    }
  }

  // answer of the delivery simulator (admin), shown like an answer of the api
  private async handleSimulated(response: DeliverySimulatorResponse, type: string, path: string, successMessage: string, showErrorMessage: boolean): Promise<any> {
    if (response.Error) {
      this.cachedError[type][path] = response.Error;
      if (showErrorMessage) {
        await (type === 'GET' ? this.toastService.presentWarning(response.Error) : this.toastService.presentError(response.Error));
      }
      return null;
    }
    await this.toastService.presentSuccess(successMessage);
    return response.Body;
  }

  async getFileBlob(path: string, params: any = {}): Promise<Blob> {
    await this.ready;
    return this.http.get(this.api + path, { responseType: 'blob', params: params }).toPromise();
  }

  async getFileText(Path: string, Params: any = {}): Promise<string> {
    await this.ready;
    return this.http.get(this.api + Path, { responseType: "text", params: Params }).toPromise();
  }

}
